import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import {
  coalesceFacilityNetworkList,
  isEmptyFacilityNetworkListStatus,
} from "./facilityNetworkListFetch";

export type FoundNpiFacility = {
  npi: string;
  facility_id: number;
  facility_name: string;
};

export type InvalidNpiRow = {
  value: string;
  row_number: number;
  reason: string;
};

export type DuplicateNpiWarning = {
  npi: string;
  status: string;
  message: string;
};

export type WontSendRow = {
  identifier: string;
  reason: string;
  status?: string;
};

export type BatchNpiLookupResult = {
  found: FoundNpiFacility[];
  not_found: string[];
  invalid: InvalidNpiRow[];
  duplicate_warnings: DuplicateNpiWarning[];
  will_send?: FoundNpiFacility[];
  save_for_invite?: string[];
  wont_send?: WontSendRow[];
  /** Registry names for identifiers not registered in UniComply (NPPES/CMS). */
  facility_name_by_npi?: Record<string, string>;
};

export type SingleNpiLookupResult = {
  npi: string;
  found?: boolean;
  duplicate?: boolean;
  status?: string;
  message?: string;
  facility_id?: number;
  facility_name?: string;
};

export type ConfirmChildLinkResult = {
  success: boolean;
  message: string;
  link_requests_sent: number;
  action_needed_saved: number;
  invalid_skipped: number;
  request_ids: number[];
  wont_send_rows?: WontSendRow[];
};

export type ChildLinkRequestItem = {
  id: number;
  target_npi: string;
  status: string;
  child_facility_id?: number | null;
  facility_name?: string | null;
  invite_email?: string | null;
  expires_at?: string | null;
  created_at?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
  facility_type?: string | null;
  city?: string | null;
  state?: string | null;
  location?: string | null;
  npi_number?: string | null;
  ccn_number?: string | null;
  /** Three-party coordination — C sees conditional-pending tracker (Figma 9227:111011) */
  coordination_mode?: boolean;
  coordination_unlink_id?: number | null;
  coordination_stage?: "awaiting_child" | "awaiting_parent" | null;
  current_parent_facility_id?: number | null;
  current_parent_facility_name?: string | null;
};

export type IncomingChildLinkRequestItem = {
  id: number;
  parent_facility_id: number;
  parent_facility_name: string;
  parent_npi?: string | null;
  parent_ccn?: string | null;
  parent_facility_type?: string | null;
  parent_city?: string | null;
  parent_state?: string | null;
  parent_location?: string | null;
  parent_facility_group_id?: string | null;
  target_npi: string;
  status: string;
  expires_at?: string | null;
  created_at?: string | null;
  resolved_at?: string | null;
  resolution_note?: string | null;
  /** AB#10946 — conflict-aware accept when child already has parent B */
  coordination_mode?: boolean;
  current_parent_facility_id?: number | null;
  current_parent_facility_name?: string | null;
  /** Set after A accepts conflict link — unlink to B is in flight (row → Awaiting) */
  coordination_unlink_id?: number | null;
};

function apiBase(): string {
  const baseUrl = getApiOrigin().replace(/\/$/, "");
  if (!baseUrl) {
    throw new Error("REACT_APP_API_BASEURL is not configured.");
  }
  return baseUrl;
}

async function parseJsonResponse(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text();
  return text ? (JSON.parse(text) as Record<string, unknown>) : {};
}

function extractError(json: Record<string, unknown>, response: Response): string {
  const detail = json.detail;
  if (detail && typeof detail === "object" && detail !== null) {
    const message = (detail as Record<string, unknown>).message;
    if (typeof message === "string") return message;
  }
  if (typeof json.message === "string") return json.message;
  if (typeof json.detail === "string") return json.detail;
  return `Request failed with status ${response.status}`;
}

function unwrapData<T>(json: Record<string, unknown>): T {
  const data = json.data;
  if (data && typeof data === "object") return data as T;
  return json as T;
}

export async function batchLookupNpis(
  parentFacilityId: number,
  npiNumbers: string[],
): Promise<BatchNpiLookupResult> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/batch-lookup`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ npi_numbers: npiNumbers }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return unwrapData<BatchNpiLookupResult>(json);
}

export async function uploadBatchLookupNpis(
  parentFacilityId: number,
  file: File,
): Promise<BatchNpiLookupResult> {
  const form = new FormData();
  form.append("file", file);
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/batch-lookup/upload`,
    {
      method: "POST",
      body: form,
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return unwrapData<BatchNpiLookupResult>(json);
}

export async function lookupSingleNpi(
  parentFacilityId: number,
  npi: string,
): Promise<SingleNpiLookupResult> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/lookup-npi`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ npi }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return unwrapData<SingleNpiLookupResult>(json);
}

export async function confirmChildLinkRequests(
  parentFacilityId: number,
  foundNpis: string[],
  notFoundNpis: string[],
  inviteEmailByNpi?: Record<string, string>,
  skipNpis?: string[],
): Promise<ConfirmChildLinkResult> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        found_npis: foundNpis,
        not_found_npis: notFoundNpis,
        will_send: foundNpis,
        save_for_invite: notFoundNpis,
        skip: skipNpis || [],
        invite_email_by_npi: inviteEmailByNpi,
      }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Requests processed.",
    link_requests_sent: Number(json.link_requests_sent ?? 0),
    action_needed_saved: Number(json.action_needed_saved ?? 0),
    invalid_skipped: Number(json.invalid_skipped ?? 0),
    request_ids: Array.isArray(json.request_ids)
      ? json.request_ids.filter((id): id is number => typeof id === "number")
      : [],
    wont_send_rows: Array.isArray(json.wont_send_rows) ? (json.wont_send_rows as WontSendRow[]) : undefined,
  };
}

export type LinkedFacilityApiRow = {
  facility_id?: number | null;
  facility_name?: string | null;
  npi_number?: string | null;
  ccn_number?: string | null;
  facility_type?: string | null;
  facility_type_slug?: string | null;
  city?: string | null;
  state?: string | null;
  address_1?: string | null;
  status: "linked" | "pending" | "declined" | string;
  request_id?: number | null;
  linked_at?: string | null;
  penalty_exposure?: number | null;
};

export async function listLinkedFacilities(
  parentFacilityId: number,
): Promise<LinkedFacilityApiRow[]> {
  const url = `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/linked-facilities`;
  return coalesceFacilityNetworkList(url, async () => {
    const response = await authenticatedFetch(url);
    if (isEmptyFacilityNetworkListStatus(response.status)) return [];
    const json = await parseJsonResponse(response);
    if (!response.ok) throw new Error(extractError(json, response));
    const data = unwrapData<{ facilities?: LinkedFacilityApiRow[] }>(json);
    return Array.isArray(data.facilities) ? data.facilities : [];
  });
}

export async function listChildLinkRequests(
  parentFacilityId: number,
  status?: string,
): Promise<ChildLinkRequestItem[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  const url = `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests${query}`;
  return coalesceFacilityNetworkList(url, async () => {
    const response = await authenticatedFetch(url);
    if (isEmptyFacilityNetworkListStatus(response.status)) return [];
    const json = await parseJsonResponse(response);
    if (!response.ok) throw new Error(extractError(json, response));
    const data = unwrapData<{ requests?: ChildLinkRequestItem[] }>(json);
    return Array.isArray(data.requests) ? data.requests : [];
  });
}

export async function listIncomingChildLinkRequests(
  childFacilityId: number,
): Promise<IncomingChildLinkRequestItem[]> {
  const url = `${apiBase()}/api/v1/facility-management/facility/${childFacilityId}/child-link-requests/incoming`;
  return coalesceFacilityNetworkList(url, async () => {
    const response = await authenticatedFetch(url);
    if (isEmptyFacilityNetworkListStatus(response.status)) return [];
    const json = await parseJsonResponse(response);
    if (!response.ok) throw new Error(extractError(json, response));
    const data = unwrapData<{ requests?: IncomingChildLinkRequestItem[] }>(json);
    return Array.isArray(data.requests) ? data.requests : [];
  });
}

export async function acceptIncomingChildLinkRequest(
  childFacilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${childFacilityId}/child-link-requests/${requestId}/accept`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Link request accepted.",
  };
}

export async function declineIncomingChildLinkRequest(
  childFacilityId: number,
  requestId: number,
  reason?: string,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${childFacilityId}/child-link-requests/${requestId}/decline`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason?.trim() }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Link request declined.",
  };
}

export async function cancelChildLinkRequest(
  parentFacilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/${requestId}/cancel`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Link request cancelled.",
  };
}

export async function resendChildLinkRequest(
  parentFacilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/${requestId}/resend`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Request resent.",
  };
}

export async function dismissActionNeededRequest(
  parentFacilityId: number,
  requestId: number,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/${requestId}/dismiss`,
    { method: "POST" },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Placeholder dismissed.",
  };
}

export async function provideInviteEmail(
  parentFacilityId: number,
  requestId: number,
  email: string,
): Promise<{ success: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${apiBase()}/api/v1/facility-management/facility/${parentFacilityId}/child-link-requests/${requestId}/provide-email`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    },
  );
  const json = await parseJsonResponse(response);
  if (!response.ok) throw new Error(extractError(json, response));
  return {
    success: Boolean(json.success),
    message: typeof json.message === "string" ? json.message : "Invite sent.",
  };
}
export async function sendChildLinkRequests(
  parentFacilityId: number,
  npis: string[],
): Promise<{ success: boolean; message: string; request_ids?: number[] }> {
  const lookup = await batchLookupNpis(parentFacilityId, npis);
  const result = await confirmChildLinkRequests(
    parentFacilityId,
    lookup.found.map((f) => f.npi),
    lookup.not_found,
  );
  return {
    success: result.success,
    message: result.message,
    request_ids: result.request_ids,
  };
}

export function downloadNpiCsv(
  filename: string,
  headers: string[],
  rows: string[][],
): void {
  const lines = [headers.join(","), ...rows.map((row) => row.map((c) => `"${c.replace(/"/g, '""')}"`).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
